import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PhoneCall, ChevronDown, ChevronUp } from "lucide-react";
import { CallNotes } from "@/components/TransferCallLog";

const TONE = {
  appointment_scheduled: "bg-emerald-100 text-emerald-800", transferred_to_mlo: "bg-emerald-100 text-emerald-800",
  no_answer: "bg-slate-100 text-slate-700", voicemail: "bg-slate-100 text-slate-700",
  rejected: "bg-red-100 text-red-700", not_interested: "bg-red-100 text-red-700", do_not_call: "bg-red-100 text-red-700",
  mlo_unavailable: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-700",
};
const dur = (ms) => (ms ? `${Math.floor(ms / 60000)}:${String(Math.round((ms % 60000) / 1000)).padStart(2, "0")}` : "—");

const CallRow = ({ c, labels, admin, reload }) => {
  const [open, setOpen] = useState(false);
  const label = c.outcome ? labels[c.outcome] || c.outcome : c.status === "ongoing" ? "In progress" : ["ended", "analyzed"].includes(c.status) ? "Getting result…" : "Calling…";
  return (
    <div className="p-4" data-testid={`call-row-${c.id}`}>
      <button className="flex w-full items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`call-toggle-${c.id}`}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600"><PhoneCall className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900">{c.prospect_name || c.to_number}</span>
            <Badge className={`border-0 ${TONE[c.outcome] || "bg-blue-100 text-blue-800"}`} data-testid={`call-outcome-${c.id}`}>{label}</Badge>
            {c.source === "deity" && <Badge variant="outline" className="text-[10px]">AI Autopilot</Badge>}
          </div>
          <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()} · {c.to_number} · {dur(c.duration_ms)}{admin && c.mlo_name ? ` · MLO: ${c.mlo_name}` : ""}</div>
          {c.from_number && <div className="text-xs text-slate-400" data-testid={`call-from-${c.id}`}>Dialed from {c.from_number}</div>}
          {c.appointment && <div className="mt-0.5 text-xs font-semibold text-emerald-700">Appointment: {c.appointment}</div>}
          {c.auto_voicemail && <div className="mt-0.5 text-xs text-slate-500" data-testid={`call-auto-vm-${c.id}`}>{c.auto_voicemail}</div>}
          {c.outcome === "appointment_scheduled" && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {c.appointment_unparsed && <Badge variant="outline" className="border-amber-300 text-[10px] text-amber-800" data-testid={`call-needs-time-${c.id}`}>Needs a time — no reminder</Badge>}
              {c.reminders?.["1h"] && <Badge variant="outline" className="text-[10px]" data-testid={`call-reminder-1h-${c.id}`}>1-hour reminder sent</Badge>}
              {c.reminders?.["15m"] && <Badge variant="outline" className="text-[10px]" data-testid={`call-reminder-15m-${c.id}`}>15-min reminder sent</Badge>}
            </div>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {c.recording_url
        ? <audio controls preload="none" src={c.recording_url} className="mt-2 h-9 w-full pl-12" data-testid={`call-recording-${c.id}`} />
        : c.outcome && <p className="mt-1 pl-12 text-xs text-slate-400" data-testid={`call-no-recording-${c.id}`}>No recording (call wasn't answered)</p>}
      {c.recording_url && <div className="mt-2 pl-12"><CallNotes c={c} reload={reload} /></div>}
      {open && (
        <div className="mt-3 space-y-3 pl-12 text-sm">
          {c.summary && <p className="text-slate-700">{c.summary}</p>}
          {c.transcript ? <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{c.transcript}</pre>
            : <p className="text-xs text-slate-400">No transcript yet.</p>}
        </div>
      )}
    </div>
  );
};

export default function CallLog() {
  const { user } = useAuth();
  const [outcome, setOutcome] = useState("");
  const [d, setD] = useState({ calls: [], outcomes: {} });
  const load = () => api.get("/calls", { params: outcome ? { outcome } : {} }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [outcome]); // eslint-disable-line react-hooks/exhaustive-deps
  const tabs = [["", "All"], ...Object.entries(d.outcomes)];
  return (
    <div>
      <PageHeader title="AI Call Log" subtitle={user?.role === "admin" ? "Every AI call across all MLOs." : "Every AI call to your prospects and how it ended."} />
      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setOutcome(k)} data-testid={`call-tab-${k || "all"}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${outcome === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
        ))}
      </div>
      <Card className="divide-y divide-slate-100" data-testid="call-log-list">
        {d.calls.length === 0 && <div className="flex flex-col items-center gap-2 p-12 text-center text-slate-400"><PhoneCall className="h-9 w-9" /><p className="text-sm" data-testid="call-log-empty">No AI calls yet.</p></div>}
        {d.calls.map((c) => <CallRow key={c.id} c={c} labels={d.outcomes} admin={user?.role === "admin"} reload={load} />)}
      </Card>
    </div>
  );
}
